import { useEffect, useState } from "react";
import api, { fmtMXNFull, fmtMXN, EXPENSE_CATEGORIES, EXPENSE_PAYMENT_METHODS } from "@/lib/api";
import IncontiLogo from "@/components/IncontiLogo";
import { Printer, ArrowLeft, Download, AlertTriangle, TrendingUp, Wallet, FileText, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CATEGORY_LABEL = Object.fromEntries(EXPENSE_CATEGORIES.map((c) => [c.value, c.label]));
const PAYMENT_LABEL = {
  ...Object.fromEntries(EXPENSE_PAYMENT_METHODS.map((m) => [m.value, m.label])),
  sin_definir: "Sin definir",
};
const PAYMENT_COLOR = {
  efectivo: "#16a34a",
  transferencia: "#0891b2",
  deposito: "#2563eb",
  tarjeta_credito: "#d97706",
  tarjeta_debito: "#a16207",
  cheque: "#7c3aed",
  otro: "#64748b",
  sin_definir: "#94a3b8",
};
const STATUS_COLOR = {
  planificacion: "#94a3b8",
  en_progreso: "#f472b6",
  pausado: "#d97706",
  completado: "#16a34a",
  cancelado: "#dc2626",
};

export default function GlobalReport() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const today = new Date();
  // Default to PREVIOUS month
  const defaultMonth = today.getMonth() === 0 ? 12 : today.getMonth();
  const defaultYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
  const [mYear, setMYear] = useState(defaultYear);
  const [mMonth, setMMonth] = useState(defaultMonth);
  const [monthlyBusy, setMonthlyBusy] = useState(false);
  const [showMonthlyMenu, setShowMonthlyMenu] = useState(false);
  const [monthlyStatus, setMonthlyStatus] = useState(null);

  const fetchReport = async () => {
    try {
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      const res = await api.get("/reports/global", { params });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo cargar el reporte");
    }
  };

  useEffect(() => { fetchReport(); /* eslint-disable-next-line */ }, []);

  const downloadExcel = async () => {
    const params = {};
    if (start) params.start_date = start;
    if (end) params.end_date = end;
    const res = await api.get("/reports/global/excel", { params, responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "reporte_global.xlsx";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const downloadReimbursementsPDF = async () => {
    const params = {};
    if (start) params.start_date = start;
    if (end) params.end_date = end;
    const res = await api.get("/reports/reimbursements/pdf", { params, responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `reembolsos-${new Date().toISOString().slice(0, 10)}.pdf`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const fetchMonthlyStatus = async () => {
    try {
      const { data } = await api.get("/reports/monthly-executive/status");
      setMonthlyStatus(data || {});
    } catch { /* no-op */ }
  };

  const downloadMonthlyPDF = async () => {
    setMonthlyBusy(true);
    try {
      const res = await api.get("/reports/monthly-executive/pdf", {
        params: { year: mYear, month: mMonth },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `reporte_ejecutivo_${mYear}-${String(mMonth).padStart(2, "0")}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("PDF descargado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo generar el PDF");
    } finally {
      setMonthlyBusy(false);
    }
  };

  const sendMonthlyByEmail = async () => {
    if (!confirm(`¿Enviar por correo el reporte ejecutivo de ${mMonth}/${mYear} a todos los administradores?`)) return;
    setMonthlyBusy(true);
    try {
      const { data } = await api.post("/reports/monthly-executive/send-now", null, {
        params: { year: mYear, month: mMonth },
      });
      toast.success(`Enviado a ${data.count} de ${data.total_admins} administradores`);
      fetchMonthlyStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar el correo");
    } finally {
      setMonthlyBusy(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "contador") {
      fetchMonthlyStatus();
    }
  }, [user?.role]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-slate-300">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400 label-mono">Cargando…</div>;

  const t = data.totals;
  const utilizationPct = t.total_budget > 0 ? Math.min(100, Math.round((t.total_committed / t.total_budget) * 100)) : 0;
  const folio = `RPT-GLOBAL-${new Date().getTime().toString().slice(-8)}`;

  return (
    <div className="min-h-screen" style={{ background: "#0a0f1d" }}>
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .report-page { box-shadow: none !important; margin: 0 !important; max-width: 100% !important; }
        }
      `}</style>

      <div className="no-print sticky top-0 z-10 px-4 lg:px-6 py-3 flex items-center justify-between flex-wrap gap-3"
        style={{ borderBottom: "1px solid var(--border)", background: "rgba(5,8,18,0.9)", backdropFilter: "blur(8px)" }}>
        <button data-testid="global-report-back-btn" onClick={() => window.history.back()} className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" data-testid="global-report-start" value={start} onChange={(e) => setStart(e.target.value)}
            className="input-cyber px-2 py-1 text-xs" />
          <span className="text-slate-500 text-xs">→</span>
          <input type="date" data-testid="global-report-end" value={end} onChange={(e) => setEnd(e.target.value)}
            className="input-cyber px-2 py-1 text-xs" />
          <button data-testid="global-report-apply-btn" onClick={fetchReport} className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm">
            Aplicar
          </button>
          <button data-testid="global-report-excel-btn" onClick={downloadExcel} className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2"
            style={{ borderColor: "var(--magenta)", color: "var(--magenta)" }}>
            <Download className="w-4 h-4" /> Excel
          </button>
          <button data-testid="reimbursements-pdf-btn" onClick={downloadReimbursementsPDF} className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2"
            style={{ borderColor: "var(--green, #16a34a)", color: "var(--green, #16a34a)" }}
            title="Reporte de caja chica: gastos adelantados por personal agrupados por persona">
            <Wallet className="w-4 h-4" /> Caja chica
          </button>
          {(user?.role === "admin" || user?.role === "contador") && (
            <div className="relative">
              <button
                data-testid="monthly-executive-btn"
                onClick={() => setShowMonthlyMenu((s) => !s)}
                className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2"
                style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}
                title="Reporte ejecutivo mensual: utilidad por obra, avance físico vs financiero y resumen administrativo"
              >
                <FileText className="w-4 h-4" /> Reporte mensual
              </button>
              {showMonthlyMenu && (
                <div
                  data-testid="monthly-executive-menu"
                  className="absolute right-0 mt-2 panel-strong p-3 z-20 w-72"
                  style={{ borderColor: "rgba(34,211,238,0.4)" }}
                >
                  <div className="label-mono text-[10px] mb-2" style={{ color: "var(--cyan)" }}>
                    // REPORTE EJECUTIVO MENSUAL
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <select
                      data-testid="monthly-month"
                      value={mMonth}
                      onChange={(e) => setMMonth(parseInt(e.target.value, 10))}
                      className="input-cyber px-2 py-1.5 text-xs"
                    >
                      {["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"].map((m, i) => (
                        <option key={i + 1} value={i + 1} style={{ background: "#0a0f1d" }}>{m}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      data-testid="monthly-year"
                      value={mYear}
                      min="2024"
                      max="2099"
                      onChange={(e) => setMYear(parseInt(e.target.value, 10) || defaultYear)}
                      className="input-cyber px-2 py-1.5 text-xs"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      data-testid="monthly-download-pdf"
                      onClick={downloadMonthlyPDF}
                      disabled={monthlyBusy}
                      className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center justify-center gap-2"
                      style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}
                    >
                      {monthlyBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Descargar PDF
                    </button>
                    {user?.role === "admin" && (
                      <button
                        data-testid="monthly-send-email"
                        onClick={sendMonthlyByEmail}
                        disabled={monthlyBusy}
                        className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center justify-center gap-2"
                        style={{ borderColor: "var(--magenta)", color: "var(--magenta)" }}
                      >
                        {monthlyBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Enviar por correo
                      </button>
                    )}
                  </div>
                  {monthlyStatus?.last_yyyymm && (
                    <div className="text-[10px] text-slate-400 mt-3 leading-snug">
                      Último envío automático: <span className="text-slate-200">{monthlyStatus.last_yyyymm}</span>
                      {monthlyStatus.sent_to?.length > 0 && (
                        <div className="text-slate-500 mt-1 truncate" title={monthlyStatus.sent_to.join(", ")}>
                          → {monthlyStatus.sent_to.length} admin(s)
                        </div>
                      )}
                    </div>
                  )}
                  <div className="text-[10px] text-slate-500 mt-2">
                    El envío automático ocurre 1 vez al mes (mes anterior) al consultar el dashboard.
                  </div>
                </div>
              )}
            </div>
          )}
          <button data-testid="global-report-print-btn" onClick={() => window.print()} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2">
            <Printer className="w-4 h-4" /> Imprimir / PDF
          </button>
        </div>
      </div>

      <div className="py-8 px-4">
        <div className="report-page mx-auto bg-white text-slate-900 max-w-[920px] shadow-2xl">
          {/* Letterhead */}
          <div className="px-6 lg:px-10 pt-10 pb-6 border-b-4" style={{ borderColor: "#3F2A91" }}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <IncontiLogo size={56} />
                <div className="mt-3 text-xs text-slate-500 leading-snug">
                  CORPORATION INCONTI · administracion@inconti.net
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">// FOLIO</div>
                <div className="font-mono text-lg font-bold text-slate-900">{folio}</div>
                <div className="mt-2 text-[11px] uppercase tracking-widest text-slate-400 font-mono">// EMITIDO</div>
                <div className="font-mono text-xs text-slate-700">{new Date().toLocaleDateString("es-MX")}</div>
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="px-6 lg:px-10 py-6 text-center">
            <h1 className="text-2xl lg:text-3xl font-display font-black tracking-widest" style={{ color: "#3F2A91" }}>
              REPORTE FINANCIERO GLOBAL
            </h1>
            <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">
              Consolidado de todas las obras
            </div>
            {(start || end) && (
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1 font-mono">
                Período: {start || "inicio"} → {end || "actual"}
              </div>
            )}
          </div>

          {/* KPIs */}
          <div className="px-6 lg:px-10 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Obras activas" value={`${t.active_projects} / ${t.project_count}`} color="#3F2A91" />
              <Kpi label="Presupuesto" value={fmtMXNFull(t.total_budget)} color="#0f172a" />
              <Kpi label="Comprometido" value={fmtMXNFull(t.total_committed)} color="#3F2A91" />
              <Kpi label="Saldo global" value={fmtMXNFull(t.remaining)} color={t.remaining >= 0 ? "#16a34a" : "#dc2626"} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
              <Kpi label="Pendiente" value={fmtMXNFull(t.total_pending)} color="#d97706" small />
              <Kpi label="Aprobado" value={fmtMXNFull(t.total_approved)} color="#16a34a" small />
              <Kpi label="Pagado" value={fmtMXNFull(t.total_paid)} color="#0891b2" small />
            </div>
            <div className="mt-3 px-3 py-2 text-xs" style={{ background: "#f8fafc", border: "1px solid #e5e7eb" }}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-slate-700">Utilización global del presupuesto</span>
                <span className="font-mono font-bold tabular-nums" style={{ color: utilizationPct > 90 ? "#dc2626" : utilizationPct > 75 ? "#d97706" : "#3F2A91" }}>{utilizationPct}%</span>
              </div>
              <div className="h-2 rounded" style={{ background: "#e5e7eb" }}>
                <div className="h-full rounded" style={{ width: `${utilizationPct}%`, background: utilizationPct > 90 ? "#dc2626" : utilizationPct > 75 ? "#d97706" : "#3F2A91" }} />
              </div>
            </div>
          </div>

          {/* Cash flow KPIs */}
          <div className="px-6 lg:px-10 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Ingresos cobrados" value={fmtMXNFull(t.total_income || 0)} color="#16a34a" />
              <Kpi label="Gastos pagados" value={fmtMXNFull(t.total_paid)} color="#dc2626" />
              <Kpi label="Nómina pagada" value={fmtMXNFull(t.payroll_paid || 0)} color="#0891b2" />
              <Kpi label="Flujo de caja neto" value={fmtMXNFull(t.net_cashflow || 0)} color={(t.net_cashflow || 0) >= 0 ? "#16a34a" : "#dc2626"} />
            </div>
          </div>

          {/* Cashflow monthly chart */}
          <CashflowChart data={data.cashflow || []} />

          {/* En riesgo */}
          {data.at_risk.length > 0 && (
            <div className="px-6 lg:px-10 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4" style={{ color: "#dc2626" }} />
                <h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: "#dc2626" }}>Obras en riesgo (utilización ≥ 75%)</h2>
              </div>
              <table className="w-full text-xs border" style={{ borderColor: "#cbd5e1" }} data-testid="at-risk-table">
                <thead>
                  <tr style={{ background: "#fee2e2" }}>
                    <th className="text-left px-2 py-1.5 font-semibold">Obra</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Comprometido</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Presupuesto</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Utilización</th>
                  </tr>
                </thead>
                <tbody>
                  {data.at_risk.map((p) => (
                    <tr key={p.id} className="border-t" style={{ borderColor: "#e5e7eb" }}>
                      <td className="px-2 py-1.5">
                        <div className="font-semibold">{p.code} · {p.name}</div>
                        <div className="text-slate-500 text-[10px]">{p.client}</div>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums">{fmtMXNFull(p.committed)}</td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums">{fmtMXNFull(p.budget)}</td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums font-bold" style={{ color: p.utilization > 90 ? "#dc2626" : "#d97706" }}>
                        {p.utilization}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Por categoría */}
          {Object.keys(data.by_category).length > 0 && (
            <div className="px-6 lg:px-10 mb-6">
              <h2 className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: "#3F2A91" }}>Gastos por categoría</h2>
              <table className="w-full text-sm border" style={{ borderColor: "#cbd5e1" }}>
                <thead>
                  <tr style={{ background: "#3F2A91", color: "white" }}>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-widest">Categoría</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-widest">Monto</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-widest">% del total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => {
                    const totalAll = Object.values(data.by_category).reduce((s, v) => s + v, 0);
                    const pct = totalAll > 0 ? ((amt / totalAll) * 100).toFixed(1) : "0";
                    return (
                      <tr key={cat} className="border-t" style={{ borderColor: "#e5e7eb" }}>
                        <td className="px-3 py-2 capitalize">{CATEGORY_LABEL[cat] || cat}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMXNFull(amt)}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-500">{pct}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Por método de pago (solo gastos PAGADOS) */}
          {data.by_payment_method && Object.keys(data.by_payment_method).length > 0 && (
            <div className="px-6 lg:px-10 mb-6">
              <h2 className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: "#3F2A91" }}>
                Gastos pagados por método de pago
              </h2>
              <PaymentMethodDonut data={data.by_payment_method} />
            </div>
          )}

          {/* Por proyecto */}
          <div className="px-6 lg:px-10 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4" style={{ color: "#3F2A91" }} />
              <h2 className="text-sm font-bold uppercase tracking-widest" style={{ color: "#3F2A91" }}>Detalle por proyecto ({data.by_project.length})</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border" style={{ borderColor: "#cbd5e1" }} data-testid="by-project-table">
                <thead>
                  <tr style={{ background: "#f1f5f9" }}>
                    <th className="text-left px-2 py-1.5 font-semibold">Obra</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Estado</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Presupuesto</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Aprobado</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Pagado</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Comprometido</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Saldo</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Util.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_project.map((p) => (
                    <tr key={p.id} className="border-t" style={{ borderColor: "#e5e7eb" }}>
                      <td className="px-2 py-1.5">
                        <div className="font-semibold">{p.code}</div>
                        <div className="text-slate-500 text-[10px] truncate max-w-[180px]">{p.name}</div>
                      </td>
                      <td className="px-2 py-1.5">
                        <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase" style={{ background: STATUS_COLOR[p.status] + "20", color: STATUS_COLOR[p.status] }}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmtMXN(p.budget)}</td>
                      <td className="px-2 py-1.5 text-right font-mono" style={{ color: "#16a34a" }}>{fmtMXN(p.approved)}</td>
                      <td className="px-2 py-1.5 text-right font-mono" style={{ color: "#0891b2" }}>{fmtMXN(p.paid)}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-bold">{fmtMXN(p.committed)}</td>
                      <td className="px-2 py-1.5 text-right font-mono" style={{ color: p.remaining < 0 ? "#dc2626" : "#0f172a" }}>{fmtMXN(p.remaining)}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-bold" style={{ color: p.utilization > 90 ? "#dc2626" : p.utilization > 75 ? "#d97706" : "#3F2A91" }}>
                        {p.utilization}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 lg:px-10 py-4 text-center text-[10px] text-slate-400 border-t mt-6" style={{ borderColor: "#e5e7eb" }}>
            Documento generado por Corporation Inconti ERP · {folio} · {new Date().toLocaleDateString("es-MX")}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, color, small }) {
  return (
    <div className={`px-3 ${small ? "py-2" : "py-2.5"} rounded`} style={{ border: "1px solid #cbd5e1", background: "#f8fafc" }}>
      <div className="text-[9px] uppercase tracking-widest font-mono text-slate-500">{label}</div>
      <div className={`font-display font-black tabular-nums mt-0.5 ${small ? "text-base" : "text-lg"}`} style={{ color }}>{value}</div>
    </div>
  );
}

function CashflowChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="px-6 lg:px-10 mb-6">
        <h2 className="text-sm font-bold uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color: "#3F2A91" }}>
          <TrendingUp className="w-4 h-4" /> Flujo de caja mensual
        </h2>
        <div className="text-sm text-slate-500 italic px-3 py-4 text-center" style={{ background: "#f8fafc" }}>
          Sin movimientos aún. Registra ingresos y paga gastos para ver el flujo de caja por mes.
        </div>
      </div>
    );
  }

  // Layout
  const W = 880;
  const H = 240;
  const padL = 60, padR = 20, padT = 20, padB = 40;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const maxVal = Math.max(
    1,
    ...data.map((m) => Math.max(m.income || 0, m.outflow || 0))
  );
  const barGroupW = innerW / data.length;
  const barW = Math.max(8, Math.min(28, barGroupW / 2.6));

  // Y axis ticks (4)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((p) => Math.round(maxVal * p));
  const fmtTick = (v) => v >= 1_000_000 ? `${(v/1_000_000).toFixed(1)}M` : v >= 1000 ? `${Math.round(v/1000)}K` : String(v);

  // Net line points
  const netVals = data.map((m) => m.net || 0);
  const netMin = Math.min(0, ...netVals);
  const netMax = Math.max(0, ...netVals);
  const netSpan = (netMax - netMin) || 1;
  const netY = (v) => padT + innerH - ((v - netMin) / netSpan) * innerH;
  const netPath = data.map((m, i) => {
    const x = padL + barGroupW * i + barGroupW / 2;
    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${netY(m.net || 0).toFixed(1)}`;
  }).join(" ");

  // Totals
  const totalIn = data.reduce((s, m) => s + (m.income || 0), 0);
  const totalOut = data.reduce((s, m) => s + (m.outflow || 0), 0);
  const totalNet = totalIn - totalOut;

  const monthLabel = (k) => {
    if (!k) return "";
    const [y, m] = k.split("-");
    const names = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
    return `${names[Number(m) - 1] || m} ${y.slice(2)}`;
  };

  return (
    <div className="px-6 lg:px-10 mb-6" data-testid="cashflow-chart">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h2 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2" style={{ color: "#3F2A91" }}>
          <TrendingUp className="w-4 h-4" /> Flujo de caja mensual
        </h2>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest font-mono text-slate-600">
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "#16a34a" }} /> Ingresos</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "#dc2626" }} /> Salidas</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5" style={{ background: "#3F2A91" }} /> Neto</span>
        </div>
      </div>

      <div className="border rounded" style={{ borderColor: "#cbd5e1", background: "#f8fafc" }}>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
          {/* Grid lines + Y labels */}
          {ticks.map((v, i) => {
            const y = padT + innerH - (i / 4) * innerH;
            return (
              <g key={`tick-${i}`}>
                <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#e5e7eb" strokeWidth={1} />
                <text x={padL - 6} y={y + 3} fontSize="9" textAnchor="end" fill="#94a3b8" fontFamily="monospace">{fmtTick(v)}</text>
              </g>
            );
          })}

          {/* Bars */}
          {data.map((m, i) => {
            const xc = padL + barGroupW * i + barGroupW / 2;
            const inH = ((m.income || 0) / maxVal) * innerH;
            const outH = ((m.outflow || 0) / maxVal) * innerH;
            const baseY = padT + innerH;
            return (
              <g key={`bar-${m.month}`}>
                <rect x={xc - barW - 1} y={baseY - inH} width={barW} height={inH} fill="#16a34a" rx={2} />
                <rect x={xc + 1} y={baseY - outH} width={barW} height={outH} fill="#dc2626" rx={2} />
                <text x={xc} y={H - padB + 15} fontSize="10" textAnchor="middle" fill="#64748b" fontFamily="monospace">{monthLabel(m.month)}</text>
              </g>
            );
          })}

          {/* Zero line for net */}
          {netMin < 0 && (
            <line x1={padL} y1={netY(0)} x2={W - padR} y2={netY(0)} stroke="#94a3b8" strokeDasharray="3,3" strokeWidth={1} />
          )}

          {/* Net line */}
          {data.length > 1 && (
            <path d={netPath} fill="none" stroke="#3F2A91" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
          )}
          {/* Net points */}
          {data.map((m, i) => {
            const x = padL + barGroupW * i + barGroupW / 2;
            const y = netY(m.net || 0);
            return <circle key={`np-${m.month}`} cx={x} cy={y} r={3.5} fill="#3F2A91" stroke="white" strokeWidth={1.5} />;
          })}
        </svg>
      </div>

      {/* Monthly table */}
      <table className="w-full text-xs border mt-3" style={{ borderColor: "#cbd5e1" }} data-testid="cashflow-table">
        <thead>
          <tr style={{ background: "#3F2A91", color: "white" }}>
            <th className="text-left px-2 py-1.5 font-semibold">Mes</th>
            <th className="text-right px-2 py-1.5 font-semibold">Ingresos</th>
            <th className="text-right px-2 py-1.5 font-semibold">Gastos</th>
            <th className="text-right px-2 py-1.5 font-semibold">Nómina</th>
            <th className="text-right px-2 py-1.5 font-semibold">Total salida</th>
            <th className="text-right px-2 py-1.5 font-semibold">Neto</th>
          </tr>
        </thead>
        <tbody>
          {data.map((m) => (
            <tr key={`row-${m.month}`} className="border-t" style={{ borderColor: "#e5e7eb" }}>
              <td className="px-2 py-1.5 font-mono">{monthLabel(m.month)}</td>
              <td className="px-2 py-1.5 text-right font-mono tabular-nums" style={{ color: "#16a34a" }}>{m.income > 0 ? `+${fmtMXN(m.income)}` : "—"}</td>
              <td className="px-2 py-1.5 text-right font-mono tabular-nums" style={{ color: "#dc2626" }}>{m.expense_paid > 0 ? `-${fmtMXN(m.expense_paid)}` : "—"}</td>
              <td className="px-2 py-1.5 text-right font-mono tabular-nums" style={{ color: "#0891b2" }}>{m.payroll_paid > 0 ? `-${fmtMXN(m.payroll_paid)}` : "—"}</td>
              <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600">{m.outflow > 0 ? `-${fmtMXN(m.outflow)}` : "—"}</td>
              <td className="px-2 py-1.5 text-right font-mono tabular-nums font-bold" style={{ color: (m.net || 0) >= 0 ? "#16a34a" : "#dc2626" }}>{fmtMXN(m.net || 0)}</td>
            </tr>
          ))}
          <tr style={{ background: "#f1f5f9", borderTop: "2px solid #3F2A91" }}>
            <td className="px-2 py-2 font-bold uppercase tracking-widest text-[11px]">TOTAL</td>
            <td className="px-2 py-2 text-right font-mono font-bold" style={{ color: "#16a34a" }}>+{fmtMXN(totalIn)}</td>
            <td colSpan={2} className="px-2 py-2 text-right font-mono font-bold" style={{ color: "#dc2626" }}>-{fmtMXN(totalOut)}</td>
            <td className="px-2 py-2 text-right font-mono font-bold text-slate-700">-{fmtMXN(totalOut)}</td>
            <td className="px-2 py-2 text-right font-mono font-black" style={{ color: totalNet >= 0 ? "#16a34a" : "#dc2626" }}>{fmtMXN(totalNet)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function PaymentMethodDonut({ data }) {
  // Build sorted segments
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  if (total <= 0) return null;

  // Donut geometry — stroke-based to keep SVG markup small and print-clean
  const radius = 60;
  const stroke = 22;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const segments = entries.map(([key, amt]) => {
    const pct = amt / total;
    const length = pct * circumference;
    const seg = {
      key,
      amt,
      pct,
      color: PAYMENT_COLOR[key] || "#64748b",
      dasharray: `${length} ${circumference - length}`,
      dashoffset: -offset,
    };
    offset += length;
    return seg;
  });

  return (
    <div className="flex flex-col md:flex-row items-start gap-6 border rounded-md p-4" style={{ borderColor: "#cbd5e1", background: "#fafafa" }}>
      <div className="flex flex-col items-center flex-shrink-0">
        <svg width="180" height="180" viewBox="0 0 180 180" data-testid="payment-method-donut">
          <circle cx="90" cy="90" r={radius} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
          {segments.map((s) => (
            <circle
              key={s.key}
              cx="90"
              cy="90"
              r={radius}
              fill="none"
              stroke={s.color}
              strokeWidth={stroke}
              strokeDasharray={s.dasharray}
              strokeDashoffset={s.dashoffset}
              transform="rotate(-90 90 90)"
            />
          ))}
          <text x="90" y="86" textAnchor="middle" fontSize="11" fill="#64748b" fontFamily="monospace">PAGADO</text>
          <text x="90" y="104" textAnchor="middle" fontSize="14" fontWeight="700" fill="#1e293b" fontFamily="monospace">{fmtMXN(total)}</text>
        </svg>
      </div>

      <div className="flex-1 w-full">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "#3F2A91", color: "white" }}>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-widest">Método</th>
              <th className="text-right px-3 py-2 text-xs uppercase tracking-widest">Monto</th>
              <th className="text-right px-3 py-2 text-xs uppercase tracking-widest">%</th>
            </tr>
          </thead>
          <tbody data-testid="payment-method-table">
            {segments.map((s) => (
              <tr key={s.key} className="border-t" style={{ borderColor: "#e5e7eb" }}>
                <td className="px-3 py-2 flex items-center gap-2">
                  <span className="inline-block rounded-sm" style={{ background: s.color, width: 12, height: 12 }} />
                  <span>{PAYMENT_LABEL[s.key] || s.key}</span>
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMXNFull(s.amt)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-500">{(s.pct * 100).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


